import { TEST_ID_ADD_PLAYERS_PANEL } from "e2e/testId";
import { GripVertical, Plus } from "lucide-react-native";
import React, { memo } from "react";

import { TonePill } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import type { SuggestionReasonCategory } from "@/generated/graphql";

import SuggestionReasonChips from "../SuggestionReasonChips";

/** Props for {@link AddPlayersRowView}. */
interface AddPlayersRowViewProps {
  /** Player id — the add target and the row's test identity. */
  readonly playerId: string;
  /** Display name. */
  readonly name: string;
  /** Pre-composed secondary line (club · position, PC1). */
  readonly meta: string;
  /**
   * SE-6945: GPR score for the inline info pill after the name ("GPR 96").
   * `null` when the player is unscored — the pill is omitted rather than
   * showing a zero.
   */
  readonly gpr?: string | null;
  /**
   * Reason categories, for Suggested rows. Omitted on filtered-result rows
   * whose player is not a suggestion candidate — a searched player has no
   * stated recommendation reason, and inventing one would be exactly the
   * fallback SE-5523 forbids.
   */
  readonly reasons?: readonly SuggestionReasonCategory[];
  /** Whether an add for this player is in flight. */
  readonly isAdding: boolean;
  /**
   * SE-6948: whether the player already sits in this pipeline. An in-list row
   * is muted, states "In list" in place of its action row, and cannot be
   * added again.
   */
  readonly isInList?: boolean;
  /** Add this player to the pipeline. */
  readonly onAdd: () => void;
  /**
   * Hide this candidate for this session. Absent on filtered-result rows —
   * there is nothing to dismiss outside the Suggested tab.
   */
  readonly onDismiss?: () => void;
}

/**
 * One suggestion card in the add-players drawer — the PC1 card, also the
 * PC7 filtered-result card.
 *
 * ## Layout, per the frames
 *
 * A bordered white card: a header row (six-dot drag glyph · name · inline
 * "GPR n" info pill), the club · position meta line, the reason chips, and
 * the action row — a compact solid-accent **+ Add** with a plain-text
 * Dismiss beside it (Suggested tab only). There is NO avatar on this card;
 * the PC frames draw the drag glyph where the old Player Card v7 drew its
 * initials disc.
 *
 * The frames also draw a CLUB OBJECTIVE / GEMINI source badge top-right.
 * No schema field states a suggestion's source (`PipelineSuggestedPlayer` in
 * `src/generated/graphql.ts` carries `bestRank`/`id`/`player`/`reasons`/
 * `snapshotTimestamp` and nothing else), so per the design→code rules that
 * element is not rendered rather than invented. First tracked on SE-6947;
 * re-flagged by SE-6947's own QA pass as SE-7092, which is a genuine backend
 * gap (add a source field to `PipelineSuggestedPlayer`), not a frontend fix.
 * @param root0 - Component props.
 * @param root0.playerId - Player id.
 * @param root0.name - Display name.
 * @param root0.meta - Secondary line (club · position).
 * @param root0.gpr - GPR score for the inline info pill (SE-6945).
 * @param root0.reasons - Reason categories (Suggested rows only).
 * @param root0.isAdding - Whether an add is in flight.
 * @param root0.isInList - Whether the player already sits in this pipeline (SE-6948).
 * @param root0.onAdd - Add callback.
 * @param root0.onDismiss - Dismiss callback (Suggested rows only).
 */
const AddPlayersRowView = ({
  playerId,
  name,
  meta,
  gpr = null,
  reasons,
  isAdding,
  isInList = false,
  onAdd,
  onDismiss,
}: AddPlayersRowViewProps) => (
  <Box
    testID={TEST_ID_ADD_PLAYERS_PANEL.ROW(playerId)}
    className={`rounded-md border border-outline-subtle bg-surface-base px-3 py-3 ${
      isInList ? "opacity-60" : ""
    }`.trim()}
  >
    <Stack space="2">
      {/* Header row: drag glyph · name · inline GPR pill. The glyph is the
          frame's own leading affordance — the card carries no avatar. */}
      <Stack direction="horizontal" className="items-center gap-2">
        <Icon
          testID={TEST_ID_ADD_PLAYERS_PANEL.ROW_DRAG_HANDLE(playerId)}
          as={GripVertical}
          size="sm"
          className="shrink-0 text-content-subtle"
        />
        <Text
          variant="body-strong"
          className="shrink text-content-primary"
          isTruncated
        >
          {name}
        </Text>
        {gpr === null ? null : (
          <TonePill
            tone="info-soft"
            label={`GPR ${gpr}`}
            textVariant="micro"
            className="shrink-0 px-2 py-micro"
          />
        )}
      </Stack>

      {meta === "" ? null : (
        <Text variant="caption" className="text-content-muted" isTruncated>
          {meta}
        </Text>
      )}

      {reasons === undefined ? null : (
        <SuggestionReasonChips reasons={reasons} />
      )}

      {isInList ? (
        /* SE-6948: the player is already in this pipeline — the row states it
           and offers no action. */
        <Stack direction="horizontal" className="items-center justify-end">
          <Text
            testID={TEST_ID_ADD_PLAYERS_PANEL.ROW_IN_LIST(playerId)}
            variant="caption"
            className="text-content-muted"
          >
            In list
          </Text>
        </Stack>
      ) : (
        /* Action row — a compact solid-accent Add (the frames' primary),
           with Dismiss as a plain text button beside it. */
        <Stack direction="horizontal" className="items-center gap-3">
          <Button
            testID={TEST_ID_ADD_PLAYERS_PANEL.ROW_ADD(playerId)}
            accessibilityLabel={`Add ${name} to pipeline`}
            label="Add"
            icon={Plus}
            tone="accent"
            variant="solid"
            size="sm"
            className="rounded-sm px-5"
            isLoading={isAdding}
            onPress={onAdd}
          />
          {onDismiss === undefined ? null : (
            <Button
              testID={TEST_ID_ADD_PLAYERS_PANEL.ROW_DISMISS(playerId)}
              accessibilityLabel={`Dismiss ${name} from suggestions`}
              label="Dismiss"
              variant="link"
              size="sm"
              labelTone="secondary"
              onPress={onDismiss}
            />
          )}
        </Stack>
      )}
    </Stack>
  </Box>
);

AddPlayersRowView.displayName = "AddPlayersRowView";

export default memo(AddPlayersRowView);
